import React, { useState, useEffect } from 'react';
import { alunosAPI, academiasAPI, categoriasAPI, professoresAPI, cepAPI } from '../services/api';
import Modal from '../components/Modal';
import { VINCULO_OPCOES, calcularIdade, erroVinculoIdade, montarFormDataAtleta } from '../utils/atletaVinculo';

const BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3101/api').replace('/api', '');

function Alunos() {
  const [alunos, setAlunos] = useState([]);
  const [academias, setAcademias] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [professores, setProfessores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAcademia, setFilterAcademia] = useState('');
  const [filterCategoria, setFilterCategoria] = useState('');
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [fotoFile, setFotoFile] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);

  // Estados do Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({});

  const [formData, setFormData] = useState({
    nome: '',
    vinculo: 'responsavel',
    dataNascimento: '',
    academiaId: '',
    professorId: '',
    categoriaId: '',
    faixa: '',
    grau: '0',
    peso: '',
    cpf: '',
    rg: '',
    telefone: '',
    cep: '',
    endereco: '',
    bairro: '',
    numero: '',
    complemento: '',
    cidade: '',
    estado: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [alunosRes, academiasRes, categoriasRes, professoresRes] = await Promise.all([
        alunosAPI.getAll(),
        academiasAPI.getAll(),
        categoriasAPI.getAll(),
        professoresAPI.getAll()
      ]);
      setAlunos(alunosRes.data);
      setAcademias(academiasRes.data);
      setCategorias(categoriasRes.data);
      setProfessores(professoresRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setLoading(false);
    }
  };

  const buscarCep = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length === 8) {
      setBuscandoCep(true);
      try {
        const response = await cepAPI.buscar(cepLimpo);
        setFormData(prev => ({
          ...prev,
          endereco: response.data.endereco || '',
          bairro: response.data.bairro || '',
          cidade: response.data.cidade || '',
          estado: response.data.estado || ''
        }));
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      } finally {
        setBuscandoCep(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const erroValidacao = erroVinculoIdade(formData.vinculo, formData.dataNascimento);
    if (erroValidacao) {
      setModalConfig({
        type: 'error',
        title: 'Vínculo inválido',
        message: erroValidacao,
        onConfirm: () => setModalOpen(false)
      });
      setModalOpen(true);
      return;
    }
    try {
      const idade = calcularIdade(formData.dataNascimento);
      const dados = montarFormDataAtleta({ ...formData, idade }, fotoFile);
      if (editingId) {
        await alunosAPI.update(editingId, dados);
        setModalConfig({
          type: 'success',
          title: 'Aluno Atualizado!',
          message: 'As informações do aluno foram atualizadas com sucesso.',
          onConfirm: () => {
            setModalOpen(false);
            loadData();
            resetForm();
          }
        });
      } else {
        await alunosAPI.create(dados);
        setModalConfig({
          type: 'success',
          title: 'Aluno Cadastrado!',
          message: 'Novo aluno adicionado com sucesso ao sistema.',
          onConfirm: () => {
            setModalOpen(false);
            loadData();
            resetForm();
          }
        });
      }
      setModalOpen(true);
    } catch (error) {
      console.error('Erro ao salvar aluno:', error);
      setModalConfig({
        type: 'error',
        title: 'Erro ao Salvar',
        message: error.response?.data?.error || 'Não foi possível salvar as informações do aluno.\nVerifique os dados e tente novamente.',
        onConfirm: () => setModalOpen(false)
      });
      setModalOpen(true);
    }
  };

  const handleEdit = (aluno) => {
    setFormData({
      nome: aluno.nome,
      vinculo: aluno.vinculo || 'responsavel',
      dataNascimento: aluno.data_nascimento ? String(aluno.data_nascimento).slice(0, 10) : '',
      academiaId: String(aluno.academia_id || ''),
      professorId: String(aluno.professor_id || ''),
      categoriaId: String(aluno.categoria_id || ''),
      faixa: aluno.faixa,
      grau: String(aluno.grau ?? 0),
      peso: aluno.peso,
      cpf: aluno.cpf || '',
      rg: aluno.rg || '',
      telefone: aluno.telefone || '',
      cep: aluno.cep || '',
      endereco: aluno.endereco || '',
      bairro: aluno.bairro || '',
      numero: aluno.numero || '',
      complemento: aluno.complemento || '',
      cidade: aluno.cidade || '',
      estado: aluno.estado || ''
    });
    setFotoFile(null);
    setFotoPreview(aluno.foto_url ? `${BASE_URL}${aluno.foto_url}` : null);
    setEditingId(aluno.id);
    setShowForm(true);
  };

  const handleFoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFotoFile(file);
    setFotoPreview(URL.createObjectURL(file));
  };

  const handleDelete = async (id) => {
    const aluno = alunos.find(a => a.id === id);
    setModalConfig({
      type: 'confirm',
      title: 'Confirmar Exclusão',
      message: `Deseja realmente excluir o aluno "${aluno?.nome}"?\n\nEsta ação não poderá ser desfeita.`,
      showCancel: true,
      onConfirm: async () => {
        try {
          await alunosAPI.delete(id);
          loadData();
          setModalConfig({
            type: 'success',
            title: 'Sucesso',
            message: 'Aluno excluído com sucesso!',
            onConfirm: () => setModalOpen(false)
          });
          setModalOpen(true);
        } catch (error) {
          console.error('Erro ao excluir aluno:', error);
          setModalConfig({
            type: 'error',
            title: 'Erro',
            message: 'Erro ao excluir aluno. Tente novamente.',
            onConfirm: () => setModalOpen(false)
          });
          setModalOpen(true);
        }
      }
    });
    setModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      vinculo: 'responsavel',
      dataNascimento: '',
      academiaId: '',
      professorId: '',
      categoriaId: '',
      faixa: '',
      grau: '0',
      peso: '',
      cpf: '',
      rg: '',
      telefone: '',
      cep: '',
      endereco: '',
      bairro: '',
      numero: '',
      complemento: '',
      cidade: '',
      estado: ''
    });
    setFotoFile(null);
    setFotoPreview(null);
    setEditingId(null);
    setShowForm(false);
  };

  const getProfessoresDaAcademia = () => {
    if (!formData.academiaId) return [];
    return professores.filter(p => String(p.academia_id) === String(formData.academiaId));
  };

  const alunosFiltrados = alunos.filter(aluno => {
    const matchSearch = !searchTerm || 
      aluno.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (aluno.academia_nome && aluno.academia_nome.toLowerCase().includes(searchTerm.toLowerCase())) ||
      aluno.faixa.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchAcademia = !filterAcademia || String(aluno.academia_id) === String(filterAcademia);
    const matchCategoria = !filterCategoria || String(aluno.categoria_id) === String(filterCategoria);
    
    return matchSearch && matchAcademia && matchCategoria;
  });

  if (loading) return <div className="loading">Carregando...</div>;

  return (
    <div>
      <div className="page-header">
        <h1>Gerenciamento de Alunos</h1>
        <p>Cadastre e gerencie os atletas participantes</p>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancelar' : '+ Novo Aluno'}
        </button>
      </div>

      {!showForm && (
        <div className="search-filters-container" style={{ marginBottom: '20px' }}>
          <div className="search-box">
            <input
              type="text"
              placeholder="🔍 Buscar por nome, academia ou faixa..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button 
                className="clear-search"
                onClick={() => setSearchTerm('')}
                title="Limpar busca"
              >
                ✕
              </button>
            )}
          </div>
          
          <div className="filters-row">
            <select
              value={filterAcademia}
              onChange={(e) => setFilterAcademia(e.target.value)}
              className="filter-select"
            >
              <option value="">Todas as Academias</option>
              {academias.map(academia => (
                <option key={academia.id} value={academia.id}>{academia.nome}</option>
              ))}
            </select>

            <select
              value={filterCategoria}
              onChange={(e) => setFilterCategoria(e.target.value)}
              className="filter-select"
            >
              <option value="">Todas as Categorias</option>
              {categorias.map(categoria => (
                <option key={categoria.id} value={categoria.id}>{categoria.nome}</option>
              ))}
            </select>

            {(searchTerm || filterAcademia || filterCategoria) && (
              <button 
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  setSearchTerm('');
                  setFilterAcademia('');
                  setFilterCategoria('');
                }}
              >
                Limpar Filtros
              </button>
            )}
          </div>

          <div className="pagination">
            <span>Mostrando <strong>{alunosFiltrados.length}</strong> de <strong>{alunos.length}</strong> alunos</span>
          </div>
        </div>
      )}

      {showForm && (
        <div className="form-container" style={{ marginBottom: '30px' }}>
          <h2>{editingId ? 'Editar Aluno' : 'Novo Aluno'}</h2>
          <form onSubmit={handleSubmit}>
            <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#333' }}>👤 Dados Pessoais</h3>

            <div className="form-group">
              <label>Nome Completo *</label>
              <input
                type="text"
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
              <label>Vínculo com o atleta *</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {VINCULO_OPCOES.map((op) => (
                  <label
                    key={op.value}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 12px',
                      border: `1px solid ${formData.vinculo === op.value ? '#DC2626' : '#ddd'}`,
                      borderRadius: '8px', cursor: 'pointer',
                      background: formData.vinculo === op.value ? '#EEF2FD' : '#fff',
                    }}
                  >
                    <input
                      type="radio"
                      name="vinculo"
                      value={op.value}
                      checked={formData.vinculo === op.value}
                      onChange={(e) => setFormData({...formData, vinculo: e.target.value})}
                      style={{ marginTop: '3px' }}
                    />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '13.5px' }}>{op.label}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{op.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Foto do atleta (opcional)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div
                  style={{
                    width: '56px', height: '56px', borderRadius: '50%', flexShrink: 0,
                    background: fotoPreview ? `url("${fotoPreview}") center/cover no-repeat` : '#EEF2FD',
                    border: '1px solid #ddd', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#DC2626', fontWeight: 600,
                  }}
                >
                  {!fotoPreview && (formData.nome || '?').trim().charAt(0).toUpperCase()}
                </div>
                <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                  Escolher foto
                  <input type="file" accept="image/*" onChange={handleFoto} hidden />
                </label>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
              <div className="form-group">
                <label>CPF *</label>
                <input
                  type="text"
                  value={formData.cpf}
                  onChange={(e) => setFormData({...formData, cpf: e.target.value})}
                  placeholder="000.000.000-00"
                  maxLength="14"
                  required
                />
              </div>

              <div className="form-group">
                <label>RG</label>
                <input
                  type="text"
                  value={formData.rg}
                  onChange={(e) => setFormData({...formData, rg: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Telefone *</label>
                <input
                  type="text"
                  value={formData.telefone}
                  onChange={(e) => setFormData({...formData, telefone: e.target.value})}
                  placeholder="(00) 00000-0000"
                  required
                />
              </div>
            </div>

            <h3 style={{ marginTop: '20px', marginBottom: '15px', color: '#333' }}>📍 Endereço</h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '15px' }}>
              <div className="form-group">
                <label>CEP</label>
                <input
                  type="text"
                  value={formData.cep}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData({...formData, cep: value});
                    if (value.replace(/\D/g, '').length === 8) {
                      buscarCep(value);
                    }
                  }}
                  placeholder="00000-000"
                  maxLength="9"
                />
                {buscandoCep && <small>Buscando CEP...</small>}
              </div>

              <div className="form-group">
                <label>Endereço</label>
                <input
                  type="text"
                  value={formData.endereco}
                  onChange={(e) => setFormData({...formData, endereco: e.target.value})}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '15px' }}>
              <div className="form-group">
                <label>Bairro</label>
                <input
                  type="text"
                  value={formData.bairro}
                  onChange={(e) => setFormData({...formData, bairro: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Número</label>
                <input
                  type="text"
                  value={formData.numero}
                  onChange={(e) => setFormData({...formData, numero: e.target.value})}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Complemento</label>
              <input
                type="text"
                value={formData.complemento}
                onChange={(e) => setFormData({...formData, complemento: e.target.value})}
                placeholder="Apto, Casa, etc"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '15px' }}>
              <div className="form-group">
                <label>Cidade</label>
                <input
                  type="text"
                  value={formData.cidade}
                  onChange={(e) => setFormData({...formData, cidade: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Estado</label>
                <input
                  type="text"
                  value={formData.estado}
                  onChange={(e) => setFormData({...formData, estado: e.target.value.toUpperCase()})}
                  maxLength="2"
                />
              </div>
            </div>

            <h3 style={{ marginTop: '20px', marginBottom: '15px', color: '#333' }}>🥋 Dados Esportivos</h3>

            <div className="form-group">
              <label>Academia *</label>
              <select
                value={formData.academiaId}
                onChange={(e) => setFormData({...formData, academiaId: e.target.value, professorId: ''})}
                required
              >
                <option value="">Selecione uma academia</option>
                {academias.map(academia => (
                  <option key={academia.id} value={academia.id}>
                    {academia.nome}
                  </option>
                ))}
              </select>
            </div>

            {formData.academiaId && (
              <div className="form-group">
                <label>Professor/Filial *</label>
                <select
                  value={formData.professorId}
                  onChange={(e) => setFormData({...formData, professorId: e.target.value})}
                  required
                >
                  <option value="">Selecione um professor</option>
                  {getProfessoresDaAcademia().map(professor => (
                    <option key={professor.id} value={professor.id}>
                      {professor.is_filial ? `🏢 ${professor.nome_filial || professor.nome}` : `👨‍🏫 ${professor.nome}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Categoria *</label>
              <select
                value={formData.categoriaId}
                onChange={(e) => setFormData({...formData, categoriaId: e.target.value})}
                required
              >
                <option value="">Selecione uma categoria</option>
                {categorias.map(categoria => (
                  <option key={categoria.id} value={categoria.id}>
                    {categoria.nome}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Faixa *</label>
              <select
                value={formData.faixa}
                onChange={(e) => setFormData({...formData, faixa: e.target.value})}
                required
              >
                <option value="">Selecione uma faixa</option>
                <option value="Branca">Branca</option>
                <option value="Cinza">Cinza</option>
                <option value="Amarela">Amarela</option>
                <option value="Laranja">Laranja</option>
                <option value="Verde">Verde</option>
                <option value="Azul">Azul</option>
                <option value="Roxa">Roxa</option>
                <option value="Marrom">Marrom</option>
                <option value="Preta">Preta</option>
              </select>
            </div>

            <div className="form-group">
              <label>Grau (pontas na faixa)</label>
              <select
                value={formData.grau}
                onChange={(e) => setFormData({...formData, grau: e.target.value})}
              >
                {[0, 1, 2, 3, 4].map(g => (
                  <option key={g} value={g}>{g}º grau</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Peso (kg) *</label>
              <input
                type="number"
                step="0.1"
                value={formData.peso}
                onChange={(e) => setFormData({...formData, peso: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
              <label>Data de Nascimento *</label>
              <input
                type="date"
                value={formData.dataNascimento}
                onChange={(e) => setFormData({...formData, dataNascimento: e.target.value})}
                required
              />
              {formData.dataNascimento && calcularIdade(formData.dataNascimento) !== null && (
                <small>→ {calcularIdade(formData.dataNascimento)} anos</small>
              )}
              {erroVinculoIdade(formData.vinculo, formData.dataNascimento) && (
                <small style={{ color: '#c0392b', display: 'block', marginTop: '4px' }}>
                  ⚠️ {erroVinculoIdade(formData.vinculo, formData.dataNascimento)}
                </small>
              )}
            </div>

            <div className="actions">
              <button
                type="submit"
                className="btn btn-success"
                disabled={!!erroVinculoIdade(formData.vinculo, formData.dataNascimento)}
              >
                {editingId ? 'Atualizar' : 'Cadastrar'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {!showForm && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th></th>
                <th>Nome</th>
                <th>Vínculo</th>
                <th>Documentos</th>
                <th>Academia</th>
                <th>Professor/Filial</th>
                <th>Categoria</th>
                <th>Faixa</th>
                <th>Peso</th>
                <th>Idade</th>
                <th>Ações</th>
              </tr>
            </thead>
          <tbody>
            {alunosFiltrados.map(aluno => {
              const professor = professores.find(p => p.id === aluno.professor_id);
              return (
              <tr key={aluno.id}>
                <td>
                  <div
                    style={{
                      width: '34px', height: '34px', borderRadius: '50%',
                      background: aluno.foto_url ? `url("${BASE_URL}${aluno.foto_url}") center/cover no-repeat` : '#EEF2FD',
                      border: '1px solid #ddd', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#DC2626', fontWeight: 600, fontSize: '12px',
                    }}
                  >
                    {!aluno.foto_url && (aluno.nome || '?').trim().charAt(0).toUpperCase()}
                  </div>
                </td>
                <td>
                  <div>{aluno.nome}</div>
                  {aluno.telefone && (
                    <div style={{ fontSize: '0.85em', color: '#666' }}>
                      📞 {aluno.telefone}
                    </div>
                  )}
                </td>
                <td style={{ fontSize: '0.85em' }}>
                  {VINCULO_OPCOES.find((v) => v.value === (aluno.vinculo || 'responsavel'))?.label || '—'}
                </td>
                <td>
                  {(aluno.cpf || aluno.rg) ? (
                    <div style={{ fontSize: '0.85em' }}>
                      {aluno.cpf && <div>CPF: {aluno.cpf}</div>}
                      {aluno.rg && <div>RG: {aluno.rg}</div>}
                    </div>
                  ) : (
                    <span style={{ color: '#999' }}>-</span>
                  )}
                </td>
                <td>{aluno.academia_nome}</td>
                <td>
                  {professor ? (
                    <span>
                      {professor.is_filial ? '🏢' : '👨‍🏫'} {professor.nome}
                    </span>
                  ) : (
                    <span style={{ color: '#999' }}>-</span>
                  )}
                </td>
                <td>{aluno.categoria_nome}</td>
                <td>{aluno.faixa}{aluno.grau ? ` · ${aluno.grau}º grau` : ''}</td>
                <td>{aluno.peso}kg</td>
                <td>{aluno.idade} anos</td>
                <td>
                  <div className="actions">
                    <button 
                      className="btn-primary" 
                      onClick={() => handleEdit(aluno)}
                      style={{ marginRight: '8px' }}
                    >
                      Editar
                    </button>
                    <button 
                      className="btn-danger-ghost" 
                      onClick={() => handleDelete(aluno.id)}
                    >
                      Excluir
                    </button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
      
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        {...modalConfig}
      />
    </div>
  );
}

export default Alunos;
